import type { ReactNode } from "react";
// src/cockpit4/components/FinanceDashboard.tsx
// ============================================
// 财务仪表盘 - 财务中心 P0-功能2
//
// 功能：
//   1. 顶部 8 张核心 KPI 卡片（收入/利润/现金流/利润率/应收账款/应付账款/客单价/目标完成率）
//   2. 目标完成进度条 + 异常指标红色预警
//   3. 中部收入趋势图（AreaChart，支持 日/周/月/季/年 切换）
//   4. 底部结构分析（收入按产品/按来源/按客户 3 维饼图 + 成本结构饼图）
//   5. 时间范围筛选器 + 人工录入弹窗（成本/现金流/应收/应付/目标）
//
// 数据来源（白皮书铁律一：禁止假数据）：
//   1. RuntimeManager.query('revenue_engine' as any, { type: 'today_revenue' })
//      → 今日/本周/本月/本年收入、客单价、收入来源分解（自动统计）
//   2. RuntimeManager.query('revenue_engine' as any, { type: 'revenue_by_product' })
//      → 按产品线收入拆分（自动统计）
//   3. localStorage(key: xeaos_finance_dashboard_targets)
//      → 成本/现金流/应收/应付/目标 + 每日收入历史快照（人工录入 + 自动派生）
//   无数据时显示"暂无数据 / 待接入"空状态，绝不展示假数字。
//
// 权限：财务中心仅超级管理员开放（useUserStore.isSuperAdmin），非超管显示权限提示。
// ============================================

import { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Area,
  AreaChart,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from "recharts";
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Percent,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  AlertCircle,
  Database,
  ShoppingBag,
  Target,
  Lock,
  Settings,
  Plus,
  Trash2,
  X,
  Loader2,
  Activity,
  Users,
  PieChart as PieIcon,
  Coins,
  Receipt,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { useUserStore } from "../../stores/userStore";

// ============================================
// 类型定义（与 RevenueEngine/types.ts 对齐）
// ============================================

/** 收入来源分解项 */
interface SourceBreakdown {
  source: "crm_payment" | "sku_sales" | "product_runtime";
  amount: number;
  count: number;
}

/** 今日收入总览（来自 RevenueEngine） */
interface TodayRevenue {
  date: string;
  todayAmount: number;
  todayCount: number;
  weekAmount: number;
  monthAmount: number;
  yearAmount: number;
  weekOverWeek: number;
  pendingAmount: number;
  avgOrderValue: number;
  dataSource?: "real" | "partial" | "degraded";
  sourceBreakdown?: SourceBreakdown[];
  aggregatedAt?: string;
}

/** 按产品维度收入项 */
interface ProductRevenue {
  productId: string;
  productName: string;
  todayAmount: number;
  monthAmount: number;
  yearAmount: number;
  customerCount: number;
  share: number;
  growth: number;
}

/** 按产品维度收入（来自 RevenueEngine） */
interface RevenueByProduct {
  date: string;
  products: ProductRevenue[];
}

/** 成本项（人工录入） */
interface CostItem {
  id: string;
  category: string;
  amount: number;
}

/** localStorage 持久化结构（成本/目标/现金流 + 历史快照） */
interface FinanceStorage {
  /** 现金流（人工录入） */
  cashflow: number;
  /** 应收账款（人工录入） */
  accountsReceivable: number;
  /** 应付账款（人工录入） */
  accountsPayable: number;
  /** 收入目标（人工录入，本年度目标，元） */
  revenueTarget: number;
  /** 成本结构（人工录入） */
  costs: CostItem[];
  /** 每日收入快照（自动记录，用于趋势图，禁止假数据） */
  snapshots: Array<{ date: string; amount: number }>;
}

// 趋势时间范围
type TrendRange = "day" | "week" | "month" | "quarter" | "year";

// ============================================
// 常量配置
// ============================================

const STORAGE_KEY = "xeaos_finance_dashboard_targets";

const DEFAULT_STORAGE: FinanceStorage = {
  cashflow: 0,
  accountsReceivable: 0,
  accountsPayable: 0,
  revenueTarget: 0,
  costs: [],
  snapshots: [],
};

/** 趋势时间范围选项 */
const TREND_RANGES: { id: TrendRange; label: string; en: string }[] = [
  { id: "day", label: "日", en: "Daily" },
  { id: "week", label: "周", en: "Weekly" },
  { id: "month", label: "月", en: "Monthly" },
  { id: "quarter", label: "季", en: "Quarterly" },
  { id: "year", label: "年", en: "Yearly" },
];

/** 收入来源标签映射 */
const SOURCE_LABELS: Record<string, { label: string; color: string }> = {
  crm_payment: { label: "CRM 成交", color: "#10B981" },
  sku_sales: { label: "SKU 销售", color: "#06B6D4" },
  product_runtime: { label: "产品估算", color: "#A855F7" },
};

/** 饼图配色 */
const PIE_COLORS = ["#10B981", "#3B82F6", "#06B6D4", "#A855F7", "#F59E0B", "#EC4899", "#EF4444", "#8B5CF6"];
const COST_COLORS = ["#EF4444", "#F97316", "#F59E0B", "#EAB308", "#84CC16", "#06B6D4"];

// ============================================
// 工具函数
// ============================================

/** 读取 localStorage（防御性解析） */
function loadStorage(): FinanceStorage {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_STORAGE };
    const parsed = JSON.parse(raw) as Partial<FinanceStorage>;
    return {
      ...DEFAULT_STORAGE,
      ...parsed,
      costs: Array.isArray(parsed.costs) ? parsed.costs : [],
      snapshots: Array.isArray(parsed.snapshots) ? parsed.snapshots : [],
    };
  } catch {
    return { ...DEFAULT_STORAGE };
  }
}

/** 写入 localStorage */
function saveStorage(data: FinanceStorage): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // 写入失败不阻塞主流程
  }
}

/** 格式化金额（元 → 万元/元）；防御 undefined/NaN */
function formatMoney(amount: number): string {
  const num = Number(amount);
  if (!Number.isFinite(num)) return "¥ 0";
  if (Math.abs(num) >= 10000) {
    return `¥ ${(num / 10000).toFixed(2)} 万`;
  }
  return `¥ ${num.toLocaleString()}`;
}

/** 格式化百分比（0-1 → x%） */
function formatPercent(ratio: number): string {
  const num = Number(ratio);
  if (!Number.isFinite(num)) return "0%";
  return `${(num * 100).toFixed(1)}%`;
}

/**
 * 根据时间范围聚合每日快照为趋势数据（基于真实历史记录，禁止假数据）
 * - day: 每日金额
 * - week: 按周一归档汇总
 * - month: 按 YYYY-MM 汇总
 * - quarter: 按 YYYY-Qn 汇总
 * - year: 按 YYYY 汇总
 */
function buildTrend(
  snapshots: Array<{ date: string; amount: number }>,
  range: TrendRange
): Array<{ date: string; amount: number }> {
  if (!snapshots || snapshots.length === 0) return [];
  const sorted = [...snapshots].sort((a, b) => a.date.localeCompare(b.date));
  const map = new Map<string, number>();
  for (const s of sorted) {
    const d = new Date(s.date);
    if (isNaN(d.getTime())) continue;
    let key = "";
    if (range === "day") {
      key = s.date;
    } else if (range === "week") {
      // 归档到本周一
      const tmp = new Date(d);
      const day = (tmp.getDay() + 6) % 7;
      tmp.setDate(tmp.getDate() - day);
      key = tmp.toISOString().slice(0, 10);
    } else if (range === "month") {
      key = s.date.slice(0, 7);
    } else if (range === "quarter") {
      const q = Math.floor(d.getMonth() / 3);
      key = `${d.getFullYear()}-Q${q + 1}`;
    } else {
      key = String(d.getFullYear());
    }
    map.set(key, (map.get(key) || 0) + s.amount);
  }
  return Array.from(map.entries())
    .map(([date, amount]) => ({ date, amount }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

// ============================================
// KPI 卡片配置类型
// ============================================

interface KpiCard {
  key: string;
  label: string;
  enLabel: string;
  /** 大数字显示文本 */
  display: string;
  /** 原始数值（用于异常判断） */
  rawValue: number | null;
  /** 同比/环比变化（-1~1，正为升） */
  delta: number;
  /** 图标 */
  icon: typeof Wallet;
  /** 主题色 */
  color: string;
  /** 数据来源：auto=自动统计 / calc=计算 / input=人工录入 */
  source: "auto" | "calc" | "input";
  /** 数据来源标签 */
  sourceLabel: string;
  /** 是否异常（红色预警） */
  abnormal: boolean;
  /** 异常原因 */
  abnormalReason?: string;
}

// ============================================
// 主组件
// ============================================

export default function FinanceDashboard() {
  // 权限：财务中心仅超级管理员开放
  const isSuperAdmin = useUserStore((s) => s.isSuperAdmin());

  const [todayRevenue, setTodayRevenue] = useState<TodayRevenue | null>(null);
  const [byProduct, setByProduct] = useState<RevenueByProduct | null>(null);
  const [storage, setStorage] = useState<FinanceStorage>(DEFAULT_STORAGE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [lastUpdated, setLastUpdated] = useState<string>("");
  const [trendRange, setTrendRange] = useState<TrendRange>("day");
  const [editOpen, setEditOpen] = useState(false);

  // ============================================
  // 加载 RevenueEngine 真实数据
  // ============================================
  const loadFinanceData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // 轮询等待 RuntimeManager 初始化完成（30+ Runtime 全部初始化需要时间）
      let ready = false;
      for (let i = 0; i < 60; i++) {
        if (RuntimeManager.isInitialized()) {
          ready = true;
          break;
        }
        await new Promise((r) => setTimeout(r, 300));
      }
      if (!ready) {
        setError("RuntimeManager 未就绪，请稍后重试");
        return;
      }
      if (!RuntimeManager.hasRuntime("revenue_engine" as any)) {
        setError("RevenueEngine 未注册");
        return;
      }

      // 并行查询今日收入 + 按产品收入（真实数据来源）
      const [todayResp, productResp] = await Promise.all([
        RuntimeManager.query("revenue_engine" as any, { type: "today_revenue" }),
        RuntimeManager.query("revenue_engine" as any, { type: "revenue_by_product" }),
      ]);

      // 双层解包：RevenueEngineRuntime 返回 {success,data,...}，
      // 又被 RuntimeManager 包裹为 {success, data: <上述对象>}，因此真实数据在 resp.data.data
      const unwrap = (resp: any) => RuntimeManager.unwrapResponse(resp);

      const todayData = unwrap(todayResp);
      const productData = unwrap(productResp);

      if (todayData) setTodayRevenue(todayData as TodayRevenue);
      if (productData) setByProduct(productData as RevenueByProduct);

      // 两个查询都无数据时给出明确提示（不阻塞渲染）
      if (!todayData && !productData) {
        const firstErr = todayResp?.error || productResp?.error;
        if (firstErr) setError(firstErr);
      }

      // 记录今日收入快照到 localStorage（用于趋势图，禁止假数据）
      if (todayData) {
        const td = todayData as TodayRevenue;
        const today = new Date().toISOString().slice(0, 10);
        const cur = loadStorage();
        const exists = cur.snapshots.some((s) => s.date === today);
        if (!exists && Number.isFinite(td.todayAmount) && td.todayAmount > 0) {
          const next = {
            ...cur,
            snapshots: [...cur.snapshots, { date: today, amount: td.todayAmount }],
          };
          saveStorage(next);
          setStorage(next);
        } else {
          setStorage(cur);
        }
      } else {
        setStorage(loadStorage());
      }

      setLastUpdated(new Date().toLocaleString("zh-CN", { hour12: false }));
    } catch (err: any) {
      setError(err?.message || "加载财务数据失败");
      setStorage(loadStorage());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // 首次挂载读取本地存储（成本/目标/现金流/历史快照），再异步拉取真实收入
    setStorage(loadStorage());
    void loadFinanceData();
  }, [loadFinanceData]);

  // ============================================
  // 派生计算
  // ============================================

  /** 成本合计（人工录入） */
  const totalCost = useMemo(
    () => storage.costs.reduce((sum, c) => sum + (Number(c.amount) || 0), 0),
    [storage.costs]
  );
  const hasCostInput = storage.costs.length > 0 && totalCost > 0;

  /** 今日收入（真实） */
  const revenue = todayRevenue?.todayAmount ?? 0;
  const hasRevenue = !!todayRevenue && revenue > 0;

  /** 利润 = 收入 - 成本（需录入成本后计算） */
  const profit = hasCostInput ? revenue - totalCost : null;
  /** 利润率 = 利润 / 收入 */
  const profitMargin = profit !== null && revenue > 0 ? profit / revenue : null;
  /** 目标完成率 = 收入 / 目标 */
  const targetRate =
    storage.revenueTarget > 0 && revenue > 0 ? revenue / storage.revenueTarget : null;

  // ============================================
  // 异常指标预警
  // ============================================
  const abnormalList = useMemo(() => {
    const list: string[] = [];
    if (hasCostInput && profit !== null && profit < 0) {
      list.push(`利润为负（${formatMoney(profit)}），成本超过收入`);
    }
    if (profitMargin !== null && profitMargin < 0) {
      list.push(`利润率为负（${formatPercent(profitMargin)}）`);
    }
    if (storage.cashflow < 0) {
      list.push(`现金流为负（${formatMoney(storage.cashflow)}）`);
    }
    if (targetRate !== null && targetRate < 0.3) {
      list.push(`目标完成率过低（${formatPercent(targetRate)}）`);
    }
    if (storage.accountsPayable > 0 && hasRevenue && storage.accountsPayable > revenue) {
      list.push("应付账款高于当前收入，存在偿付压力");
    }
    return list;
  }, [hasCostInput, profit, profitMargin, storage.cashflow, storage.accountsPayable, targetRate, hasRevenue, revenue]);

  // ============================================
  // KPI 卡片数据
  // ============================================
  const kpiCards: KpiCard[] = useMemo(
    () => [
      {
        key: "revenue",
        label: "收入",
        enLabel: "Revenue",
        display: hasRevenue ? formatMoney(revenue) : "—",
        rawValue: revenue,
        delta: todayRevenue?.weekOverWeek ?? 0,
        icon: TrendingUp,
        color: "#10B981",
        source: "auto",
        sourceLabel: "自动统计",
        abnormal: false,
      },
      {
        key: "profit",
        label: "利润",
        enLabel: "Profit",
        display: profit !== null ? formatMoney(profit) : "需录入成本",
        rawValue: profit,
        delta: 0,
        icon: DollarSign,
        color: "#3B82F6",
        source: "calc",
        sourceLabel: "计算",
        abnormal: profit !== null && profit < 0,
        abnormalReason: profit !== null && profit < 0 ? "利润为负" : undefined,
      },
      {
        key: "cashflow",
        label: "现金流",
        enLabel: "Cash Flow",
        display: storage.cashflow !== 0 ? formatMoney(storage.cashflow) : "需录入",
        rawValue: storage.cashflow,
        delta: 0,
        icon: Activity,
        color: "#06B6D4",
        source: "input",
        sourceLabel: "人工录入",
        abnormal: storage.cashflow < 0,
        abnormalReason: storage.cashflow < 0 ? "现金流为负" : undefined,
      },
      {
        key: "profitMargin",
        label: "利润率",
        enLabel: "Profit Margin",
        display: profitMargin !== null ? formatPercent(profitMargin) : "需录入成本",
        rawValue: profitMargin,
        delta: 0,
        icon: Percent,
        color: "#A855F7",
        source: "calc",
        sourceLabel: "计算",
        abnormal: profitMargin !== null && profitMargin < 0,
        abnormalReason: profitMargin !== null && profitMargin < 0 ? "利润率为负" : undefined,
      },
      {
        key: "accountsReceivable",
        label: "应收账款",
        enLabel: "Accounts Receivable",
        display: storage.accountsReceivable !== 0 ? formatMoney(storage.accountsReceivable) : "需录入",
        rawValue: storage.accountsReceivable,
        delta: 0,
        icon: Coins,
        color: "#F59E0B",
        source: "input",
        sourceLabel: "人工录入",
        abnormal: false,
      },
      {
        key: "accountsPayable",
        label: "应付账款",
        enLabel: "Accounts Payable",
        display: storage.accountsPayable !== 0 ? formatMoney(storage.accountsPayable) : "需录入",
        rawValue: storage.accountsPayable,
        delta: 0,
        icon: Receipt,
        color: "#EF4444",
        source: "input",
        sourceLabel: "人工录入",
        abnormal: hasRevenue && storage.accountsPayable > revenue,
        abnormalReason:
          hasRevenue && storage.accountsPayable > revenue ? "应付账款高于当前收入" : undefined,
      },
      {
        key: "avgOrderValue",
        label: "客单价",
        enLabel: "Avg Order Value",
        display: todayRevenue?.avgOrderValue ? formatMoney(todayRevenue.avgOrderValue) : "—",
        rawValue: todayRevenue?.avgOrderValue ?? 0,
        delta: 0,
        icon: ShoppingBag,
        color: "#EAB308",
        source: "auto",
        sourceLabel: "自动统计",
        abnormal: false,
      },
      {
        key: "targetRate",
        label: "目标完成率",
        enLabel: "Target Rate",
        display: targetRate !== null ? formatPercent(targetRate) : "需录入目标",
        rawValue: targetRate,
        delta: 0,
        icon: Target,
        color: "#EC4899",
        source: "input",
        sourceLabel: "人工录入",
        abnormal: targetRate !== null && targetRate < 0.3,
        abnormalReason: targetRate !== null && targetRate < 0.3 ? "目标完成率过低" : undefined,
      },
    ],
    [hasRevenue, revenue, todayRevenue, profit, profitMargin, storage, targetRate, hasCostInput]
  );

  // ============================================
  // 趋势数据（基于历史快照，禁止假数据）
  // ============================================
  const trendData = useMemo(
    () => buildTrend(storage.snapshots, trendRange),
    [storage.snapshots, trendRange]
  );

  // ============================================
  // 收入结构分析（3 维度，全部真实数据）
  // ============================================

  // 维度1：按产品线（revenue_by_product yearAmount）
  const revenueByProductData = useMemo(() => {
    if (!byProduct?.products?.length) return [];
    return byProduct.products
      .filter((p) => p.yearAmount > 0)
      .map((p) => ({ name: p.productName, value: p.yearAmount }));
  }, [byProduct]);

  // 维度2：按收入来源（today_revenue.sourceBreakdown）
  const revenueBySourceData = useMemo(() => {
    if (!todayRevenue?.sourceBreakdown?.length) return [];
    return todayRevenue.sourceBreakdown
      .filter((s) => s.amount > 0)
      .map((s) => ({
        name: SOURCE_LABELS[s.source]?.label || s.source,
        value: s.amount,
      }));
  }, [todayRevenue]);

  // 维度3：按产品客户数分布（revenue_by_product customerCount）
  const revenueByCustomerData = useMemo(() => {
    if (!byProduct?.products?.length) return [];
    return byProduct.products
      .filter((p) => p.customerCount > 0)
      .map((p) => ({ name: p.productName, value: p.customerCount }));
  }, [byProduct]);

  // 成本结构（人工录入）
  const costStructureData = useMemo(() => {
    if (!storage.costs.length) return [];
    return storage.costs
      .filter((c) => c.amount > 0)
      .map((c) => ({ name: c.category, value: c.amount }));
  }, [storage.costs]);

  // ============================================
  // 录入弹窗状态
  // ============================================
  const [editForm, setEditForm] = useState({
    cashflow: "",
    accountsReceivable: "",
    accountsPayable: "",
    revenueTarget: "",
  });
  const [editCosts, setEditCosts] = useState<CostItem[]>([]);
  const [newCostCategory, setNewCostCategory] = useState("");
  const [newCostAmount, setNewCostAmount] = useState("");

  const openEdit = useCallback(() => {
    setEditForm({
      cashflow: String(storage.cashflow || ""),
      accountsReceivable: String(storage.accountsReceivable || ""),
      accountsPayable: String(storage.accountsPayable || ""),
      revenueTarget: String(storage.revenueTarget || ""),
    });
    setEditCosts(storage.costs.map((c) => ({ ...c })));
    setNewCostCategory("");
    setNewCostAmount("");
    setEditOpen(true);
  }, [storage]);

  const handleSaveEdit = useCallback(() => {
    const next: FinanceStorage = {
      ...storage,
      cashflow: Number(editForm.cashflow) || 0,
      accountsReceivable: Number(editForm.accountsReceivable) || 0,
      accountsPayable: Number(editForm.accountsPayable) || 0,
      revenueTarget: Number(editForm.revenueTarget) || 0,
      costs: editCosts.filter((c) => c.category.trim() && c.amount > 0),
    };
    saveStorage(next);
    setStorage(next);
    setEditOpen(false);
  }, [storage, editForm, editCosts]);

  const addCost = useCallback(() => {
    const cat = newCostCategory.trim();
    const amt = Number(newCostAmount) || 0;
    if (!cat || amt <= 0) return;
    setEditCosts((prev) => [
      ...prev,
      { id: `cost-${Date.now()}`, category: cat, amount: amt },
    ]);
    setNewCostCategory("");
    setNewCostAmount("");
  }, [newCostCategory, newCostAmount]);

  const removeCost = useCallback((id: string) => {
    setEditCosts((prev) => prev.filter((c) => c.id !== id));
  }, []);

  // ============================================
  // 渲染：权限校验（非超管显示权限提示）
  // ============================================
  if (!isSuperAdmin) {
    return (
      <div className="h-full w-full overflow-hidden p-4">
        <div className="h-full flex flex-col items-center justify-center rounded-xl border border-red-500/20 bg-red-500/[0.02] backdrop-blur-xl">
          <div className="w-16 h-16 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center mb-4">
            <Lock size={28} className="text-red-400" />
          </div>
          <h2 className="text-[16px] font-semibold text-white mb-2">权限受限</h2>
          <p className="text-[12px] font-mono text-gray-400 text-center max-w-sm leading-relaxed">
            财务仪表盘仅对超级管理员开放。
            <br />
            普通员工无权查看财务数据。
          </p>
          <p className="text-[10px] font-mono text-gray-600 mt-4">
            如需访问，请联系超级管理员授权。
          </p>
        </div>
      </div>
    );
  }

  // ============================================
  // 渲染：主面板
  // ============================================
  return (
    <div className="h-full w-full overflow-hidden p-4">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="h-full flex flex-col rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl overflow-hidden"
      >
        {/* ============ Header ============ */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-md bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
              <Wallet size={16} className="text-emerald-400" />
            </div>
            <div>
              <h2 className="text-[15px] font-semibold text-white tracking-tight">财务仪表盘</h2>
              <p className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
                Finance Dashboard · 收入 · 利润 · 现金流 · 结构分析
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {lastUpdated && (
              <span className="text-[9px] font-mono text-gray-600 hidden md:inline">
                {lastUpdated}
              </span>
            )}
            <button
              onClick={openEdit}
              className="flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded border border-white/[0.08] bg-white/[0.03] text-gray-300 hover:bg-white/[0.06] transition"
            >
              <Settings size={10} />
              录入
            </button>
            <button
              onClick={loadFinanceData}
              disabled={loading}
              className="flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 transition disabled:opacity-50"
            >
              {loading ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
              {loading ? "加载中" : "刷新"}
            </button>
          </div>
        </div>

        {/* ============ 内容区 ============ */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* 错误提示 */}
          {error && (
            <div className="px-4 py-2 rounded-md border border-red-500/20 bg-red-500/[0.03] text-[10px] font-mono text-red-400">
              <AlertCircle size={11} className="inline mr-1" />
              {error}
            </div>
          )}

          {/* 异常指标红色预警横幅 */}
          {abnormalList.length > 0 && (
            <div className="px-4 py-2 rounded-md border border-red-500/30 bg-red-500/[0.06] flex items-start gap-2">
              <AlertCircle size={13} className="text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-[10px] font-mono uppercase tracking-wider text-red-400 mb-1">
                  异常指标预警 · {abnormalList.length} 项
                </div>
                <ul className="space-y-0.5">
                  {abnormalList.map((item, i) => (
                    <li key={i} className="text-[11px] font-mono text-red-300">
                      · {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* ============ 8 张核心 KPI 卡片 ============ */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {kpiCards.map((card, idx) => {
              const Icon = card.icon;
              const isPositive = card.delta > 0;
              const noData = card.display === "—";
              const needInput = card.display.startsWith("需录入");
              return (
                <motion.div
                  key={card.key}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: idx * 0.05, ease: "easeOut" }}
                  className="relative rounded-xl border bg-white/[0.02] p-4 overflow-hidden"
                  style={{
                    borderColor: card.abnormal
                      ? `${card.color}80`
                      : "rgba(255,255,255,0.06)",
                    borderTopWidth: 2,
                    borderTopColor: card.abnormal ? "#EF4444" : `${card.color}40`,
                  }}
                >
                  {/* 顶部光带 */}
                  <div
                    className="absolute top-0 left-0 right-0 h-px"
                    style={{
                      background: card.abnormal
                        ? "linear-gradient(90deg, transparent, #EF4444, transparent)"
                        : `linear-gradient(90deg, transparent, ${card.color}, transparent)`,
                    }}
                  />
                  {/* 右上光晕 */}
                  <div
                    className="absolute -top-8 -right-8 w-24 h-24 rounded-full pointer-events-none"
                    style={{
                      background: `radial-gradient(circle, ${card.color}20 0%, transparent 70%)`,
                      filter: "blur(16px)",
                    }}
                  />

                  <div className="relative flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{
                          background: `${card.color}15`,
                          border: `1px solid ${card.color}30`,
                        }}
                      >
                        <Icon size={14} style={{ color: card.color }} />
                      </div>
                      <div>
                        <h3 className="text-[12px] font-medium text-white">{card.label}</h3>
                        <p className="text-[8px] font-mono text-gray-500 uppercase tracking-wider">
                          {card.enLabel}
                        </p>
                      </div>
                    </div>
                    {/* 数据来源标签 */}
                    <span
                      className="inline-flex items-center gap-0.5 text-[8px] font-mono px-1.5 py-0.5 rounded"
                      style={{
                        background: `${card.color}12`,
                        color: card.color,
                      }}
                    >
                      <Database size={8} />
                      {card.sourceLabel}
                    </span>
                  </div>

                  {/* 大数字 48px monospaced */}
                  <div className="relative mb-2 min-h-[48px] flex items-end">
                    {noData || needInput ? (
                      <span
                        className="text-[13px] font-mono"
                        style={{ color: needInput ? "#6B7280" : "#4B5563" }}
                      >
                        {card.display}
                      </span>
                    ) : (
                      <span
                        className="text-[48px] leading-none font-mono font-bold tracking-tight"
                        style={{ color: card.abnormal ? "#EF4444" : card.color }}
                      >
                        {card.display}
                      </span>
                    )}
                  </div>

                  {/* 异常标记 / 同比环比 */}
                  <div className="relative flex items-center justify-between text-[10px] font-mono">
                    {card.abnormal ? (
                      <span className="flex items-center gap-1 text-red-400">
                        <AlertCircle size={10} />
                        {card.abnormalReason}
                      </span>
                    ) : card.delta !== 0 && !noData && !needInput ? (
                      <span
                        className={`flex items-center gap-0.5 ${
                          isPositive ? "text-emerald-400" : "text-red-400"
                        }`}
                      >
                        {isPositive ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
                        {Math.abs(card.delta * 100).toFixed(1)}%
                      </span>
                    ) : (
                      <span />
                    )}
                    {!noData && !needInput && todayRevenue?.dataSource && (
                      <span
                        className={
                          todayRevenue.dataSource === "real"
                            ? "text-emerald-400/60"
                            : todayRevenue.dataSource === "partial"
                            ? "text-amber-400/60"
                            : "text-gray-600"
                        }
                      >
                        {todayRevenue.dataSource === "real"
                          ? "● real"
                          : todayRevenue.dataSource === "partial"
                          ? "● partial"
                          : "● degraded"}
                      </span>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* ============ 目标完成进度条 ============ */}
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Target size={12} className="text-pink-400" />
                <h3 className="text-[12px] font-medium text-white">目标完成进度</h3>
                <span className="text-[9px] font-mono text-gray-500">（本年度收入目标）</span>
              </div>
              <div className="flex items-center gap-2">
                {targetRate !== null ? (
                  <>
                    <span className="text-[10px] font-mono text-gray-400">
                      {formatMoney(revenue)} / {formatMoney(storage.revenueTarget)}
                    </span>
                    <span
                      className="text-[14px] font-mono font-bold"
                      style={{ color: targetRate < 0.3 ? "#EF4444" : "#EC4899" }}
                    >
                      {formatPercent(targetRate)}
                    </span>
                  </>
                ) : (
                  <span className="text-[10px] font-mono text-gray-600">
                    需录入收入目标（点击"录入"）
                  </span>
                )}
              </div>
            </div>
            <div className="h-3 rounded-full bg-white/[0.04] overflow-hidden border border-white/[0.04]">
              {targetRate !== null ? (
                <motion.div
                  className="h-full rounded-full"
                  style={{
                    background:
                      targetRate < 0.3
                        ? "linear-gradient(90deg, #EF4444, #F97316)"
                        : "linear-gradient(90deg, #EC4899, #A855F7)",
                    width: `${Math.min(100, targetRate * 100)}%`,
                  }}
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(100, targetRate * 100)}%` }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                />
              ) : (
                <div className="h-full w-full flex items-center justify-center">
                  <span className="text-[9px] font-mono text-gray-600">暂无目标数据</span>
                </div>
              )}
            </div>
          </div>

          {/* ============ 收入趋势图（AreaChart + 时间范围切换） ============ */}
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <TrendingUp size={12} className="text-emerald-400" />
                <h3 className="text-[12px] font-medium text-white">收入趋势</h3>
                <span className="text-[9px] font-mono text-gray-500">
                  （基于每日真实收入快照聚合）
                </span>
              </div>
              {/* 时间范围切换按钮组 */}
              <div className="flex items-center gap-0.5 p-0.5 rounded-md border border-white/[0.06] bg-white/[0.02]">
                {TREND_RANGES.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => setTrendRange(r.id)}
                    className={`px-2 py-1 rounded text-[10px] font-mono transition ${
                      trendRange === r.id
                        ? "bg-emerald-500/20 text-emerald-300"
                        : "text-gray-500 hover:text-gray-300 hover:bg-white/[0.04]"
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
            {trendData.length > 0 ? (
              <div className="h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trendData} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                    <defs>
                      <linearGradient id="financeRevenueGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10B981" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#10B981" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#1e2a3f" strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: "#9CA3AF", fontSize: 10, fontFamily: "monospace" }}
                      stroke="rgba(255,255,255,0.06)"
                    />
                    <YAxis
                      tick={{ fill: "#9CA3AF", fontSize: 10, fontFamily: "monospace" }}
                      stroke="rgba(255,255,255,0.06)"
                      tickFormatter={(v: any) => {
                        const n = Number(v);
                        return Math.abs(n) >= 10000
                          ? `${(n / 10000).toFixed(0)}万`
                          : String(n);
                      }}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "rgba(10,14,26,0.95)",
                        border: "1px solid rgba(16,185,129,0.3)",
                        borderRadius: "6px",
                        fontSize: "11px",
                        fontFamily: "monospace",
                      }}
                      labelStyle={{ color: "#E5E7EB" }}
                      itemStyle={{ color: "#10B981" }}
                      formatter={(value: any) => [formatMoney(Number(value)), "收入"]}
                    />
                    <Area
                      type="monotone"
                      dataKey="amount"
                      stroke="#10B981"
                      strokeWidth={1.5}
                      fill="url(#financeRevenueGradient)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[260px] flex flex-col items-center justify-center text-gray-600">
                <AlertCircle size={20} className="mb-2" />
                <span className="text-[11px] font-mono">暂无历史数据 · 待接入</span>
                <span className="text-[9px] font-mono text-gray-700 mt-1">
                  每日首次刷新会自动记录当日收入快照，逐步形成趋势
                </span>
              </div>
            )}
          </div>

          {/* ============ 结构分析：收入 3 维 + 成本结构 ============ */}
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
            <div className="flex items-center gap-2 mb-3">
              <PieIcon size={12} className="text-purple-400" />
              <h3 className="text-[12px] font-medium text-white">结构分析</h3>
              <span className="text-[9px] font-mono text-gray-500">
                （收入按产品/来源/客户 + 成本结构）
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
              {/* 收入按产品 */}
              <StructurePie
                title="收入按产品"
                icon={<ShoppingBag size={11} className="text-emerald-400" />}
                data={revenueByProductData}
                colors={PIE_COLORS}
                valueFormatter={formatMoney}
                emptyHint="暂无产品收入数据 · 待 RevenueEngine 接入"
              />
              {/* 收入按来源 */}
              <StructurePie
                title="收入按来源"
                icon={<Database size={11} className="text-cyan-400" />}
                data={revenueBySourceData}
                colors={["#10B981", "#06B6D4", "#A855F7"]}
                valueFormatter={formatMoney}
                emptyHint="暂无来源分解数据 · 待 CRM/SKU 接入"
              />
              {/* 收入按客户数分布 */}
              <StructurePie
                title="客户数分布"
                icon={<Users size={11} className="text-blue-400" />}
                data={revenueByCustomerData}
                colors={PIE_COLORS}
                valueFormatter={(v) => `${v} 客户`}
                emptyHint="暂无客户分布数据 · 待产品 Runtime 接入"
              />
              {/* 成本结构 */}
              <StructurePie
                title="成本结构"
                icon={<Receipt size={11} className="text-red-400" />}
                data={costStructureData}
                colors={COST_COLORS}
                valueFormatter={formatMoney}
                emptyHint="暂无成本数据 · 点击「录入」添加成本项"
              />
            </div>
          </div>

          {/* ============ 数据来源说明 ============ */}
          <div className="px-4 py-2 rounded-md border border-blue-500/15 bg-blue-500/[0.02] text-[10px] font-mono text-blue-300/70">
            <Database size={11} className="inline mr-1" />
            数据来源：RevenueEngineRuntime（today_revenue + revenue_by_product）自动统计 +
            localStorage（xeaos_finance_dashboard_targets）人工录入成本/现金流/目标
            {todayRevenue?.aggregatedAt && (
              <span className="ml-2 text-gray-500">
                · 最后聚合：{new Date(todayRevenue.aggregatedAt).toLocaleTimeString("zh-CN")}
              </span>
            )}
          </div>
        </div>
      </motion.div>

      {/* ============ 人工录入弹窗 ============ */}
      <AnimatePresence>
        {editOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setEditOpen(false)}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-6"
          >
            <motion.div
              initial={{ scale: 0.95, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-lg max-h-[85vh] flex flex-col rounded-xl border border-white/[0.08] bg-[#0a0e1a] overflow-hidden"
            >
              {/* 弹窗头部 */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
                <div className="flex items-center gap-2">
                  <Settings size={14} className="text-emerald-400" />
                  <h3 className="text-[14px] font-semibold text-white">财务数据录入</h3>
                </div>
                <button
                  onClick={() => setEditOpen(false)}
                  className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.05]"
                >
                  <X size={16} />
                </button>
              </div>

              {/* 弹窗内容 */}
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                {/* 数值录入区 */}
                <div className="grid grid-cols-2 gap-3">
                  <EditField
                    label="现金流"
                    value={editForm.cashflow}
                    onChange={(v) => setEditForm((f) => ({ ...f, cashflow: v }))}
                    placeholder="单位：元"
                  />
                  <EditField
                    label="收入目标"
                    value={editForm.revenueTarget}
                    onChange={(v) => setEditForm((f) => ({ ...f, revenueTarget: v }))}
                    placeholder="本年度目标，元"
                  />
                  <EditField
                    label="应收账款"
                    value={editForm.accountsReceivable}
                    onChange={(v) => setEditForm((f) => ({ ...f, accountsReceivable: v }))}
                    placeholder="单位：元"
                  />
                  <EditField
                    label="应付账款"
                    value={editForm.accountsPayable}
                    onChange={(v) => setEditForm((f) => ({ ...f, accountsPayable: v }))}
                    placeholder="单位：元"
                  />
                </div>

                {/* 成本结构录入 */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-mono text-gray-400 uppercase tracking-wider">
                      成本结构
                    </span>
                    <span className="text-[9px] font-mono text-gray-600">
                      合计：{formatMoney(editCosts.reduce((s, c) => s + c.amount, 0))}
                    </span>
                  </div>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={newCostCategory}
                      onChange={(e) => setNewCostCategory(e.target.value)}
                      placeholder="成本类别（如：人力/服务器/营销）"
                      className="flex-1 px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-white placeholder-gray-600 focus:outline-none focus:border-emerald-500/40"
                    />
                    <input
                      type="number"
                      value={newCostAmount}
                      onChange={(e) => setNewCostAmount(e.target.value)}
                      placeholder="金额"
                      className="w-24 px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-white placeholder-gray-600 focus:outline-none focus:border-emerald-500/40"
                    />
                    <button
                      onClick={addCost}
                      className="px-2.5 py-1.5 rounded-md bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 transition"
                    >
                      <Plus size={12} />
                    </button>
                  </div>
                  <div className="space-y-1.5 max-h-40 overflow-y-auto">
                    {editCosts.length === 0 ? (
                      <div className="text-center py-4 text-[10px] font-mono text-gray-600">
                        暂无成本项，请添加
                      </div>
                    ) : (
                      editCosts.map((c) => (
                        <div
                          key={c.id}
                          className="flex items-center justify-between px-2.5 py-1.5 rounded-md border border-white/[0.04] bg-white/[0.01]"
                        >
                          <span className="text-[11px] text-gray-200">{c.category}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] font-mono text-gray-300">
                              {formatMoney(c.amount)}
                            </span>
                            <button
                              onClick={() => removeCost(c.id)}
                              className="text-gray-500 hover:text-red-400 transition"
                            >
                              <Trash2 size={11} />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* 弹窗底部 */}
              <div className="px-5 py-3 border-t border-white/[0.06] flex items-center justify-between">
                <span className="text-[9px] font-mono text-gray-600">
                  数据将保存到 localStorage（{STORAGE_KEY}）
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setEditOpen(false)}
                    className="px-3 py-1.5 text-[11px] font-mono text-gray-400 hover:text-white bg-white/[0.03] hover:bg-white/[0.06] rounded transition"
                  >
                    取消
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    className="px-3 py-1.5 text-[11px] font-mono text-emerald-300 bg-emerald-500/15 border border-emerald-500/30 hover:bg-emerald-500/25 rounded transition"
                  >
                    保存
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================
// 子组件：结构分析饼图
// ============================================

interface StructurePieProps {
  title: string;
  icon: ReactNode;
  data: Array<{ name: string; value: number }>;
  colors: string[];
  valueFormatter: (v: number) => string;
  emptyHint: string;
}

function StructurePie({ title, icon, data, colors, valueFormatter, emptyHint }: StructurePieProps) {
  return (
    <div className="rounded-lg border border-white/[0.04] bg-white/[0.01] p-3">
      <div className="flex items-center gap-1.5 mb-2">
        {icon}
        <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">{title}</span>
      </div>
      {data.length > 0 ? (
        <div className="h-[180px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={65}
                paddingAngle={2}
                dataKey="value"
                nameKey="name"
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={colors[index % colors.length]}
                    stroke="rgba(10,14,26,0.8)"
                    strokeWidth={1}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  background: "rgba(10,14,26,0.95)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: "6px",
                  fontSize: "11px",
                  fontFamily: "monospace",
                }}
                labelStyle={{ color: "#E5E7EB" }}
                itemStyle={{ color: "#9CA3AF" }}
                formatter={(value: any, name: any) => [valueFormatter(Number(value)), String(name)]}
              />
              <Legend
                wrapperStyle={{ fontSize: "9px", fontFamily: "monospace" }}
                iconType="circle"
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-[180px] flex flex-col items-center justify-center text-center">
          <AlertCircle size={16} className="text-gray-700 mb-1.5" />
          <span className="text-[10px] font-mono text-gray-600 leading-relaxed px-2">
            {emptyHint}
          </span>
        </div>
      )}
    </div>
  );
}

// ============================================
// 子组件：录入字段
// ============================================

interface EditFieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}

function EditField({ label, value, onChange, placeholder }: EditFieldProps) {
  return (
    <div>
      <label className="block text-[10px] font-mono text-gray-400 uppercase tracking-wider mb-1">
        {label}
      </label>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] font-mono text-white placeholder-gray-600 focus:outline-none focus:border-emerald-500/40"
      />
    </div>
  );
}
